import Flags from "common/constants/flags";
import type { CaptureResult, PostHog } from "posthog-js";
import type { ExportPlanEvent } from "./components/builders/communities/utils/exportPlanTypes";
import type { HomePlanEvent } from "./components/builders/communities/utils/homePlanTypes";
import type { PlanLibraryEvent } from "./components/builders/plan-library/planLibraryTypes";
import type { ProcureEvent } from "./components/procure/types";
import type { ShowroomIntroEvent } from "./components/showroom2/state/showroomTypes";
import type { StudioExportEvent } from "./components/studio/analytics/studioExportTypes";

/**
 * PostHog
 *
 * See ANALYTICS.md for usage
 */

// ignore non-pageview events in admin section
const urlIgnoreList = ["/admin", "/debug"];

// Posthog records a small buffer in memory to anticipate triggering a session recording.
// Disable this behavior on urls that match to minimize memory usage
const sessionRecordingDisableList = ["/showroom/intro"];

export async function initPosthog() {
  if (!Flags.POSTHOG_API_KEY) {
    return;
  }
  // lazy load the dependency, as it's quite large
  const ph = (await import("posthog-js")).posthog;
  posthog = ph;
  if (ph.__loaded) {
    return;
  }

  const path = window.location.pathname ?? "";
  const disableSessionRecording = sessionRecordingDisableList.some((url) => path.startsWith(url));

  ph.init(Flags.POSTHOG_API_KEY, {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    person_profiles: "identified_only",
    disable_session_recording: disableSessionRecording,
    // Force canvas capture to be disabled to prevent performance issues
    session_recording: {
      captureCanvas: {
        recordCanvas: false,
      },
    },
    disable_web_experiments: true,
    capture_dead_clicks: false,
    custom_blocked_useragents: [],
    advanced_disable_feature_flags: true,
    loaded: (posthog: PostHog) => {
      posthog.debug(Flags.DEBUG);

      //  Fire off all calls that were captured before posthog loaded
      for (const event of postHogQueueCalls) {
        switch (event.call) {
          case "identify": {
            posthog.identify(...event.args);
            break;
          }
          case "reset": {
            posthog.reset(...event.args);
            break;
          }
          case "capture": {
            posthog.capture(...event.args);
            break;
          }
          case "setPersonProperties": {
            posthog.setPersonProperties(...event.args);
            break;
          }
        }
      }
    },
    before_send: (event: CaptureResult | null): CaptureResult | null => {
      if (!event) {
        return null;
      }
      // Allow all pageview events for basic usage data (and rejecting causes warning)
      if (event.event === "$pageview") {
        return event;
      }
      const path = window.location.pathname;
      if (urlIgnoreList.some((url) => path.startsWith(url))) {
        return null;
      }

      return event;
    },
  });
}

// See ANALYTICS.md for usage & event naming conventions
type AnalyticsEvent =
  | ProcureEvent
  | PlanLibraryEvent
  | ShowroomIntroEvent
  | HomePlanEvent
  | ExportPlanEvent
  | StudioExportEvent
  // TODO: These events are copied over from GA. Replace with a funnel of events for each product area/workflow.
  | "export:pdfPlanSet"
  | "export:salesBrochure"
  | "publicLink:homePlanSubmitted"
  | "publicLink:homePlanShared";

export type AnalyticsEventMetadata = Record<string, any>;

export function sendAnalyticsEvent(eventName: AnalyticsEvent, metadata?: AnalyticsEventMetadata): void {
  try {
    if (!posthog.__loaded) {
      throw new Error("PostHog not initialized.");
    }
    posthog.capture(eventName, metadata);
  } catch (e) {
    console.error(`Failed to log analytics event. Reason: ${e}`);
  }
}

/**
 * Posthog is loaded dynamically in _app. To prevent dropped events, we initialize it here with a PostHogQueue object
 * that captures all calls and then re-fires them once PostHog is loaded.
 *
 * Any additional properties accessed from posthog will need to be added here.
 */
type PostHogQueue = Pick<
  PostHog,
  "__loaded" | "capture" | "_isIdentified" | "reset" | "identify" | "setPersonProperties"
>;

type PostHogQueueCall =
  | {
      args: Parameters<PostHog["capture"]>;
      call: "capture";
    }
  | {
      args: Parameters<PostHog["reset"]>;
      call: "reset";
    }
  | {
      args: Parameters<PostHog["identify"]>;
      call: "identify";
    }
  | {
      args: Parameters<PostHog["setPersonProperties"]>;
      call: "setPersonProperties";
    };

const postHogQueueCalls: PostHogQueueCall[] = [];

const postHogQueue: PostHogQueue = {
  __loaded: true,
  _isIdentified: () => false,
  capture: (...args) => {
    postHogQueueCalls.push({ args, call: "capture" });
    return undefined;
  },
  reset: (...args) => {
    postHogQueueCalls.push({ args, call: "reset" });
  },
  identify: (...args) => {
    postHogQueueCalls.push({ args, call: "identify" });
  },
  setPersonProperties: (...args) => {
    postHogQueueCalls.push({ args, call: "setPersonProperties" });
  },
};

export let posthog: PostHog | PostHogQueue = postHogQueue;
